import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import FormItem from '../../components/form/form-item/FormItem';
import Button from '../../components/form/button/Button';
import Input from '../../components/form/input/Input';
import UnsavedChangesModal from '../components/unsaved-changes-modal/UnsavedChangesModal';
import ScreenPanel from '../components/screen-panel/ScreenPanel';
import ContentWrap from '../components/content-wrap/ContentWrap';
import { history } from '../../core/state/history';
import { RootState } from '../../core/state/state';
import ProfileInfo from './profile-info/ProfileInfo';
import { getExternalUserProfileDetails, updateExternalUserProfileDetails } from '../../core/state/users/asyncActions';
import { spawnGlobalNotification } from '../../core/state/global-notifications/asyncActions';
import { GlobalNotificationTypes } from '../../core/enums/GlobalNotificationTypes';
import { ExternalUserProfileDetails } from '../../core/state/users/types';
import { ProfileDetails } from '../../core/types/services/ProfileDetails';
import { IMG_LOCATION } from '../../core/constants/api';

const Profile: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.user.userInfo);
  const externalUserProfileDetails = useSelector((state: RootState) => state.users.externalUserProfileDetails);
  const projectId = useSelector((state: RootState) => state.user.userInfo?.projectID);
  const projectDetails = useSelector((state: RootState) => state.projects?.projectDetails);

  const validationSchema = yup
    .object()
    .shape({
      email: yup.string().required(t('validation.email.required')).email(t('validation.email.invalidFormat')).min(2)
    })
    .required();

  const {
    control,
    reset,
    formState: { isValid, errors },
    handleSubmit,
    formState
  } = useForm<ProfileDetails>({
    mode: 'onChange',
    defaultValues: {
      addressLine1: externalUserProfileDetails?.addressLine1 ?? '',
      postCode: externalUserProfileDetails?.postCode ?? '',
      city: externalUserProfileDetails?.city ?? '',
      phoneNumber: externalUserProfileDetails?.phoneNumber ?? '',
      email: externalUserProfileDetails?.email ?? '',
      name: externalUserProfileDetails?.name ?? ''
    },
    resolver: yupResolver(validationSchema)
  });

  React.useEffect(() => {
    if (user) {
      dispatch(getExternalUserProfileDetails(Number(user.userID)));
    }
  }, [user, dispatch]);

  useEffect(() => {
    if (externalUserProfileDetails) {
      reset(externalUserProfileDetails);
    }
  }, [externalUserProfileDetails, reset]);

  const onSubmit = handleSubmit(async (formData: ExternalUserProfileDetails) => {
    if (user && formData) {
      await dispatch(updateExternalUserProfileDetails(Number(user.userID), formData));
      await dispatch(getExternalUserProfileDetails(Number(user.userID)));
      dispatch(
        spawnGlobalNotification({
          type: GlobalNotificationTypes.Success,
          message: t('profile.form.message.success')
        })
      );
    }
  });

  const onCancel = () => {
    history.push(`/projects/${projectId}/bookings`);
  };

  return (
    <ContentWrap>
      <ScreenPanel title={t('profile.header.title')}>
        <form id="add-profile-details" onSubmit={onSubmit}>
          <FormItem text={t('profile.label.generalInformation')}>
            <ProfileInfo img={projectDetails?.image.name ? `${IMG_LOCATION}/${projectDetails?.image.fullName}` : undefined} />
          </FormItem>
          <FormItem text={t('profile.label.companyAddress')}>
            <div className="form-column">
              <div className="form-row mb-3">
                <Controller
                  name="addressLine1"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <Input
                      type="text"
                      onChange={(e) => {
                        onChange(e.target.value);
                      }}
                      placeholder={t('profile.placeholder.streetNameAndNumber')}
                      id="name"
                      name="name"
                      value={value ?? ''}
                    />
                  )}
                />
              </div>
              <div className="form-row mb-3">
                <Controller
                  name="postCode"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <Input
                      type="text"
                      onChange={(e) => {
                        onChange(e.target.value);
                      }}
                      placeholder={t('common.postalCode')}
                      id="postCode"
                      name="postCode"
                      value={value ?? ''}
                    />
                  )}
                />
                <Controller
                  name="city"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <Input
                      type="text"
                      onChange={(e) => {
                        onChange(e.target.value);
                      }}
                      placeholder={t('common.city')}
                      id="city"
                      name="city"
                      value={value ?? ''}
                    />
                  )}
                />
              </div>
            </div>
          </FormItem>
          <FormItem text={t('profile.label.phoneNumber')}>
            <Controller
              name="phoneNumber"
              control={control}
              render={({ field: { onChange, value } }) => (
                <Input
                  type="text"
                  onChange={(e) => {
                    onChange(e.target.value);
                  }}
                  placeholder={t('profile.placeholder.phoneNumber')}
                  id="postalCode"
                  error={errors.phoneNumber?.message}
                  name="phoneNumber"
                  value={value ?? ''}
                />
              )}
            />
          </FormItem>
          <FormItem text={`${t('common.email')} (${t('common.forLogin')})`} required>
            <Controller
              name="email"
              control={control}
              render={({ field: { onChange, value } }) => (
                <Input
                  disabled
                  type="email"
                  inputMode="email"
                  onChange={(e) => {
                    onChange(e.target.value);
                  }}
                  placeholder={t('profile.placeholder.email')}
                  id="email"
                  error={errors.email?.message}
                  name="email"
                  required
                  value={value ?? ''}
                />
              )}
            />
          </FormItem>
          <div className="row">
            <div className="offset-3 col-9">
              <Button form="add-profile-details" buttonTypes={['blue-outline', 'transparent']} className="mr-2" onClick={onCancel}>
                {t('common.cancel')}
              </Button>
              <Button disabled={!isValid} buttonTypes={['blue']} type="submit">
                {t('common.save')}
              </Button>
            </div>
          </div>
          <UnsavedChangesModal showIf={formState.isDirty} onSaveExit={() => onSubmit()} />
        </form>
      </ScreenPanel>
    </ContentWrap>
  );
};

export default Profile;
